import { formatISO, parse } from 'date-fns'
import { isEmpty, map, omit } from 'lodash'
import React, { useEffect, useState } from 'react'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { useHistory } from 'react-router-dom'
import { useTransferSearch } from '../../hooks'
import {
  Button,
  DatePickerInput,
  LabeledInput,
  LabeledSelect,
} from '@apex/component-library'
import {
  FormControl,
  FormLabel,
  Icon,
  InputProps,
  SelectProps,
  Spacer,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import {
  AcatsSearchDto,
  NsccStatus,
  NsccStatusDisplay,
  ProcessingDirection,
  ProcessingDirectionDisplay,
  RejectReason,
  RejectReasonDisplay,
  TransferSummary,
  TransferSummaryDisplay,
  TransferType,
  TransferTypeDisplay,
} from '../../api/types'

interface WrappedFilterInputProps extends InputProps {
  label: string
}

export const WrappedFilterInput = ({
  label,
  ...rest
}: WrappedFilterInputProps) => (
  <WrapItem>
    <LabeledInput {...rest} fontWeight="regular" label={label} size="md" />
  </WrapItem>
)

interface WrappedSelectProps extends SelectProps {
  label: string
  options: { value: string; label: string }[]
}

export const WrappedSelect = ({
  label,
  options,
  ...rest
}: WrappedSelectProps) => (
  <WrapItem>
    <LabeledSelect {...rest} fontWeight="regular" label={label} size="md">
      {options.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </LabeledSelect>
  </WrapItem>
)

const TransferSearchBar = () => {
  const {
    transferSearchState: { acatsSearchDto },
    searchTransfers,
  } = useTransferSearch()
  const [transferFilters, setTransferFilters] = useState<AcatsSearchDto>(
    acatsSearchDto
  )
  const [isAdvancedSearch, setAdvancedSearch] = useState<boolean>(true)
  const history = useHistory()

  useEffect(() => {
    setTransferFilters(acatsSearchDto)
  }, [acatsSearchDto])

  const handleFilterChange = (filterField: string, value: string) =>
    setTransferFilters(previousTransferFilters => {
      const acatsTransferFilters = isEmpty(value)
        ? omit(previousTransferFilters, filterField)
        : {
            ...previousTransferFilters,
            [filterField]: value,
          }
      history.replace(history.location.pathname, { acatsTransferFilters })
      return acatsTransferFilters
    })

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    searchTransfers(history, transferFilters)
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDateChange = (filterField: string, value: Date | string) => {
    const formatString = 'MM/dd/yyyy'
    if (value instanceof Date) {
      handleFilterChange(
        filterField,
        formatISO(value, { representation: 'date' })
      )
    } else if (isEmpty(value)) {
      handleFilterChange(filterField, '')
    } else if (value.trim().length === formatString.length) {
      handleFilterChange(
        filterField,
        formatISO(parse(value.trim(), formatString, new Date()), {
          representation: 'date',
        })
      )
    } else {
      handleFilterChange(filterField, value)
    }
  }

  const getDate = (value: string) => {
    const formatString = 'yyyy-MM-dd'
    if (value.length === formatString.length) {
      return parse(value, formatString, new Date())
    } else {
      return value
    }
  }

  return (
    <form>
      <Wrap
        backgroundColor="apex.whiteSmoke"
        align="flex-end"
        spacing="1rem"
        padding="1rem"
      >
        <WrappedFilterInput
          label="Account Number"
          value={transferFilters?.accountNumber || ''}
          onChange={e => handleFilterChange('accountNumber', e.target.value)}
          width="12rem"
        />
        <WrappedFilterInput
          label="Control Number"
          value={transferFilters?.acatControlNumber || ''}
          onChange={e =>
            handleFilterChange('acatControlNumber', e.target.value)
          }
          width="12rem"
        />
        {isAdvancedSearch && (
          <>
            <WrapItem>
              <FormControl>
                <FormLabel>Created Date From</FormLabel>
                <DatePickerInput
                  date={
                    transferFilters?.createdOnStart
                      ? getDate(transferFilters.createdOnStart)
                      : ''
                  }
                  disableFutureDates={true}
                  onChange={date => handleDateChange('createdOnStart', date)}
                  width="12rem"
                />
              </FormControl>
            </WrapItem>
            <WrapItem>
              <FormControl>
                <FormLabel>Created Date To</FormLabel>
                <DatePickerInput
                  date={
                    transferFilters?.createdOnEnd
                      ? getDate(transferFilters.createdOnEnd)
                      : ''
                  }
                  disableFutureDates={true}
                  onChange={date => handleDateChange('createdOnEnd', date)}
                  width="12rem"
                />
              </FormControl>
            </WrapItem>
            <WrappedSelect
              label="NSCC Status"
              placeholder=" "
              value={transferFilters?.nsccStatus || ''}
              onChange={e => handleFilterChange('nsccStatus', e.target.value)}
              options={map(NsccStatus, value => ({
                value,
                label: NsccStatusDisplay[value],
              }))}
              width="12rem"
            />
            <WrappedSelect
              label="Transfer Direction"
              placeholder=" "
              value={transferFilters?.processingDirection || ''}
              onChange={e =>
                handleFilterChange('processingDirection', e.target.value)
              }
              options={map(ProcessingDirection, value => ({
                value,
                label: ProcessingDirectionDisplay[value],
              }))}
              width="12rem"
            />
            <WrappedSelect
              label="Transfer Type"
              placeholder=" "
              value={transferFilters?.transferType || ''}
              onChange={e => handleFilterChange('transferType', e.target.value)}
              options={map(TransferType, value => ({
                value,
                label: TransferTypeDisplay[value],
              }))}
              width="12rem"
            />
            <WrappedSelect
              label="Reject Reason"
              placeholder=" "
              value={transferFilters?.rejectReason || ''}
              onChange={e => handleFilterChange('rejectReason', e.target.value)}
              options={map(RejectReason, value => ({
                value,
                label: RejectReasonDisplay[value],
              }))}
              width="12rem"
            />
            <WrappedSelect
              label="Transfer Status"
              placeholder=" "
              value={transferFilters?.transferSummary || ''}
              onChange={e =>
                handleFilterChange('transferSummary', e.target.value)
              }
              options={map(TransferSummary, value => ({
                value,
                label: TransferSummaryDisplay[value],
              }))}
              width="12rem"
            />
          </>
        )}
        <WrapItem>
          <Button variant="primary" type="submit" onClick={handleSubmit}>
            Search
          </Button>
        </WrapItem>
        {isAdvancedSearch && (
          <WrapItem>
            <Button
              variant="link"
              border={0}
              color="apex.cornflowerBlue"
              px={5}
              onClick={() => {
                setTransferFilters({})
              }}
            >
              Clear
            </Button>
          </WrapItem>
        )}
        <Spacer />
        <WrapItem>
          <Button
            variant="link"
            border={0}
            color="apex.cornflowerBlue"
            px={5}
            onClick={() =>
              setAdvancedSearch(currentlyCollapsed => !currentlyCollapsed)
            }
          >
            {isAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
            <Icon as={isAdvancedSearch ? FaChevronUp : FaChevronDown} ml={2} />
          </Button>
        </WrapItem>
      </Wrap>
    </form>
  )
}

export default TransferSearchBar
